import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RssFeedIcon from "@mui/icons-material/RssFeedOutlined";
import FiberNewIcon from "@mui/icons-material/FiberNewOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { TableSkeleton } from "../TableSkeleton";
import { EditDrawer, type Dismiss } from "../EditDrawer";
import {
  foyerApi,
  type ContentPackageRow,
  type ContentSupplierRow,
  type FeedScanItem,
  type FeedSyncProgressView,
} from "../../http/foyer";
import { friendlyError } from "../../utils/apiError";
import { describeError, useToast } from "../toast";
import { Trans, useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import {
  describeFeedAssetPlan,
  planFeedAsset,
  type FeedAssetValueSource,
} from "@shared/mrss-feed-asset";

/**
 * The review queue's Feed view (`[[mrss-feed-content-providers]]`).
 *
 * A provider with an MRSS feed, a Scan button, a diff against what has
 * already been ingested, and a review-then-approve drawer per new item -
 * see the plan for why approving mints the asset, registers the ingest job
 * and starts copying the master in ONE request (`feedIngest.ts`), with no
 * separate `content_submissions` hold: this drawer's own edit-then-Approve
 * IS the review, the same way a staff-authored `VodWizardDialog` title never
 * needs one either.
 */

export default function FeedReviewView() {
  const [suppliers, setSuppliers] = useState<ContentSupplierRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState<ContentSupplierRow | null>(null);
  const { t } = useTranslation("content");

  useEffect(() => {
    let alive = true;
    foyerApi
      .listContentSuppliers()
      .then((res) => {
        if (!alive) return;
        // Only what this view can act on. A provider with no feed simply
        // never appears here - the same "no button where nothing is behind
        // it" stance the rest of this codebase takes.
        setSuppliers(res.suppliers.filter((s) => s.mrssFeedUrl));
      })
      .catch((e) => alive && setError(friendlyError(t, e)));
    return () => {
      alive = false;
    };
  }, [t]);

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Card sx={{ overflow: "hidden" }}>
        {!suppliers ? (
          <TableSkeleton rows={3} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("feedReviewView.columns.provider")}</TableCell>
                  <TableCell>{t("feedReviewView.columns.feed")}</TableCell>
                  <TableCell sx={{ width: 120 }} align="right">
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Box sx={{ py: 5, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {t("feedReviewView.noFeeds")}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {suppliers.map((s) => (
                  <TableRow key={s.id} data-testid={`feed-supplier-${s.id}`}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "fontWeightSemibold" }}
                      >
                        {s.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block", maxWidth: 420 }}
                      >
                        {s.mrssFeedUrl}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        data-testid={`feed-scan-${s.id}`}
                        onClick={() => setScanning(s)}
                      >
                        {t("feedReviewView.scan")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {scanning && (
        <FeedScanDialog supplier={scanning} onClose={() => setScanning(null)} />
      )}
    </>
  );
}

/**
 * The diff: every item the feed carries, marked against what this flow has
 * already turned into a real title.
 */
function FeedScanDialog({
  supplier,
  onClose,
}: {
  supplier: ContentSupplierRow;
  onClose: () => void;
}) {
  const [items, setItems] = useState<FeedScanItem[] | null>(null);
  const { t } = useTranslation("content");
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<ContentPackageRow[]>([]);
  const [channelName, setChannelName] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<FeedScanItem | null>(null);
  const [syncing, setSyncing] = useState(false);
  // 🚨 Reported by Sigge: a full feed of a couple hundred items "feels long
  // for 83" new ones - the already-in rows are not actionable (no onClick,
  // nothing to review) and the stat cards already answer "how many", so by
  // default the table shows only the delta. Never hidden outright though -
  // an operator confirming a specific title already made it in still needs
  // to be able to look.
  //
  // Two earlier cuts of the control that flips this: a single button whose
  // own label changed state (not discoverable), then a separate New/All
  // toggle row (a second "New"/"All" next to the stat cards already saying
  // the same words). Settled on making the stat cards THE control - one
  // set of numbers, doubling as the filter, selection shown on the card
  // itself rather than in a second place naming the same things.
  const [view, setView] = useState<"new" | "all" | "in">("new");

  const load = useCallback(() => {
    setItems(null);
    setError(null);
    foyerApi
      .scanSupplierFeed(supplier.id)
      .then((res) => setItems(res.items))
      .catch((e) => setError(friendlyError(t, e)));
  }, [supplier.id, t]);

  useEffect(() => {
    load();
    foyerApi
      .listContentPackages()
      .then((res) => setPackages(res.packages))
      .catch(() => setPackages([]));
  }, [load]);

  // Named once, just for the reviewer's confidence that Approve does what
  // it says - the approval itself never needs the name, only the id already
  // on `supplier.effectChannelId`.
  useEffect(() => {
    if (!supplier.effectChannelId) return;
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn({ kind: "global" })
      .then((rows) => {
        if (!alive) return;
        setChannelName(
          rows.find((p) => p.id === supplier.effectChannelId)?.name ?? null
        );
      })
      .catch(() => alive && setChannelName(null));
    return () => {
      alive = false;
    };
  }, [supplier.effectChannelId]);

  // Out first - that is the actual work this dialog exists for, and a
  // provider's feed can carry hundreds of already-ingested items ahead of
  // the handful worth a reviewer's time.
  const sorted = items
    ? [...items].sort((a, b) => Number(a.in) - Number(b.in))
    : null;
  const newCount = items ? items.filter((i) => !i.in).length : 0;
  const inCount = items ? items.length - newCount : 0;
  // Already-in rows are not actionable - no onClick, nothing to review - and
  // the stat cards above already answer "how many", so they are hidden by
  // default rather than padding the table with rows that feel long for a
  // handful of new titles.
  const visible = !sorted
    ? null
    : view === "new"
      ? sorted.filter((i) => !i.in)
      : view === "in"
        ? sorted.filter((i) => i.in)
        : sorted;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          typography: "titleSm",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{t("feedReviewView.feedOf", { name: supplier.name })}</span>
        <Stack direction="row" alignItems="center" spacing={1}>
          {newCount > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSyncing(true)}
              data-testid="feed-sync-open"
            >
              {t("feedReviewView.syncAllNew", { count: newCount })}
            </Button>
          )}
          <IconButton onClick={onClose} aria-label={t("feedReviewView.close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {/* The count itself is the point of scanning at all - an operator
            deciding whether a rescan is worth their time should not have to
            count rows to find out, so it leads the dialog rather than
            captioning the table under it. */}
        {items && (
          <Stack
            direction="row"
            gap={1.5}
            sx={{ mb: 3 }}
            data-testid="feed-scan-stats"
          >
            <StatCard
              icon={<RssFeedIcon sx={{ fontSize: 30 }} color="action" />}
              value={items.length}
              label={t("feedReviewView.stat.inFeed", { count: items.length })}
              testId="feed-scan-stat-total"
              selected={view === "all"}
              onClick={() => setView("all")}
            />
            <StatCard
              icon={<FiberNewIcon sx={{ fontSize: 30 }} color="warning" />}
              value={newCount}
              label={t("feedReviewView.stat.new")}
              testId="feed-scan-stat-new"
              selected={view === "new"}
              onClick={() => setView("new")}
            />
            <StatCard
              icon={<CheckCircleIcon sx={{ fontSize: 30 }} color="success" />}
              value={inCount}
              label={t("feedReviewView.stat.alreadyIn")}
              testId="feed-scan-stat-in"
              selected={view === "in"}
              onClick={() => setView("in")}
            />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {channelName && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="feed-target-channel"
          >
            <Trans
              t={t}
              i18nKey="feedReviewView.joinsChannel"
              values={{ channel: channelName }}
              components={{ strong: <strong /> }}
            />
          </Alert>
        )}
        {!sorted ? (
          <TableSkeleton rows={4} />
        ) : sorted.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("feedReviewView.noItems")}
            </Typography>
          </Box>
        ) : (
          <>
            {visible && visible.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {view === "in"
                    ? t("feedReviewView.nothingApproved")
                    : t("feedReviewView.nothingNew")}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 64 }} />
                      <TableCell>{t("feedReviewView.columns.title")}</TableCell>
                      <TableCell sx={{ width: 110 }}>
                        {t("feedReviewView.columns.status")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(visible ?? []).map((item) => (
                      <TableRow
                        key={item.guid}
                        hover={!item.in}
                        data-testid={`feed-item-${item.guid}`}
                        sx={item.in ? undefined : { cursor: "pointer" }}
                        onClick={item.in ? undefined : () => setReviewing(item)}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              width: 48,
                              height: 32,
                              borderRadius: "6px",
                              overflow: "hidden",
                              bgcolor: tokens.rowAlt,
                            }}
                          >
                            {item.thumbnailUrl && (
                              <Box
                                component="img"
                                src={item.thumbnailUrl}
                                alt=""
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {item.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={item.in ? "success" : "default"}
                            label={
                              item.in
                                ? t("feedReviewView.inLibrary")
                                : t("feedReviewView.new")
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      {syncing && (
        <SyncFeedDialog
          supplierId={supplier.id}
          channelName={channelName}
          newCount={newCount}
          packages={packages}
          onClose={() => {
            setSyncing(false);
            // Whatever the run approved is now "in": a rescan says so.
            load();
          }}
        />
      )}

      {reviewing && (
        <ReviewFeedItemDrawer
          supplierId={supplier.id}
          channelName={channelName}
          item={reviewing}
          packages={packages}
          onClose={() => setReviewing(null)}
          // Runs after the drawer has slid out (it dismisses itself first).
          onApproved={() => {
            setReviewing(null);
            // Moves the row to "in library" without a full rescan - a
            // provider's feed can be a few hundred items, and the operator
            // just watched this one get approved.
            setItems((prev) =>
              prev
                ? prev.map((i) =>
                    i.guid === reviewing.guid ? { ...i, in: true } : i
                  )
                : prev
            );
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * Approve every new item of the feed in one go.
 *
 * The run happens on the server and outlives this dialog; what is shown here is
 * only its tally, polled. Each approval becomes an ingest job tagged
 * `mrss_sync`, and the Ingest status page is where they are followed once
 * they are queued - the dialog links there rather than growing a second
 * progress view.
 *
 * The package picker only exists for a supplier with NO effect channel, for the
 * same reason as in the single-item drawer.
 */
function SyncFeedDialog({
  supplierId,
  channelName,
  newCount,
  packages,
  onClose,
}: {
  supplierId: string;
  channelName: string | null;
  newCount: number;
  packages: ContentPackageRow[];
  onClose: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [progress, setProgress] = useState<FeedSyncProgressView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Poll while a run is going. Stops itself the moment it reports finished.
  useEffect(() => {
    if (!progress?.running) return;
    const timer = setInterval(() => {
      foyerApi
        .feedSyncProgress(supplierId)
        .then((r) => r.progress && setProgress(r.progress))
        .catch(() => {});
    }, 1500);
    return () => clearInterval(timer);
  }, [progress?.running, supplierId]);

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const res = await foyerApi.syncSupplierFeed(supplierId, {
        packages: channelName ? [] : chosen,
      });
      setProgress(res.progress);
      toast.success(t("feedReviewView.syncStarted"));
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(describeError(t, "startFeedSync", e));
    } finally {
      setStarting(false);
    }
  }

  const done = progress && !progress.running;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ typography: "titleSm" }}>
        {t("feedReviewView.syncTitle")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!progress && (
            <>
              <Typography variant="body2">
                <Trans
                  t={t}
                  i18nKey="feedReviewView.syncIntro"
                  values={{ count: newCount }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              {channelName ? (
                <Alert severity="info">
                  <Trans
                    t={t}
                    i18nKey="feedReviewView.eachJoins"
                    values={{ channel: channelName }}
                    components={{ strong: <strong /> }}
                  />
                </Alert>
              ) : (
                <TextField
                  select
                  size="small"
                  label={t("feedReviewView.mediaCollections")}
                  value={chosen}
                  onChange={(e) =>
                    setChosen(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as unknown as string[])
                    )
                  }
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      (selected as string[])
                        .map(
                          (id) => packages.find((p) => p.id === id)?.name ?? id
                        )
                        .join(", "),
                  }}
                  helperText={t("feedReviewView.noCollectionHint")}
                  inputProps={{ "data-testid": "feed-sync-packages" }}
                >
                  {packages.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
          {progress && (
            <Box data-testid="feed-sync-progress">
              <Typography variant="body2">
                {t("feedReviewView.progress", {
                  status: progress.running
                    ? t("feedReviewView.approving")
                    : t("feedReviewView.done"),
                  approved: progress.approved,
                  total: progress.total,
                  extras:
                    (progress.skipped > 0
                      ? t("feedReviewView.skipped", { count: progress.skipped })
                      : "") +
                    (progress.failed > 0
                      ? t("feedReviewView.failed", { count: progress.failed })
                      : ""),
                })}
              </Typography>
              {progress.errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1, typography: "meta" }}>
                  {progress.errors.map((e) => (
                    <div key={e.guid}>
                      {e.guid}: {e.error}
                    </div>
                  ))}
                </Alert>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                <Trans
                  t={t}
                  i18nKey="feedReviewView.followIngest"
                  components={{ link: <a href="/distribution/ingest" /> }}
                />
              </Typography>
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="feed-sync-close">
          {done || progress
            ? t("common:buttons.close")
            : t("common:buttons.cancel")}
        </Button>
        {!progress && (
          <Button
            variant="contained"
            disabled={starting || (!channelName && chosen.length === 0)}
            onClick={start}
            data-testid="feed-sync-start"
          >
            {t("feedReviewView.approveAll", { count: newCount })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Pictures, metadata, files, then Approve.
 *
 * Prefilled from the feed, editable: an operator reviewing DroneTV's own
 * wording is not obligated to ship it verbatim. The video/thumbnail preview
 * plays the feed's OWN hosted URL directly, deliberately not the SAS-and-
 * ingest-job player `RawPreview` uses elsewhere on this page - there is no
 * ingest job yet at review time, and AirVuz's links are plain, unsigned,
 * public files (confirmed against a real feed).
 *
 * ## The media-collections picker only exists for a supplier with NO channel
 *
 * `channelName` set means this supplier's items already have a destination -
 * its effect channel - and it is the ONLY correct one: packages are the
 * guest-facing reachability mechanism, and venue-ambience content joining
 * one would be exactly the "must never reach a guest room" violation
 * `effect-ffa`'s own doctrine warns about. The picker is not merely
 * de-emphasised here, it is absent - there is nothing correct it could do
 * for this supplier. It reappears only for a supplier with no channel
 * mapped, where packages remain the real (and only) reachability answer.
 */
function ReviewFeedItemDrawer({
  supplierId,
  channelName,
  item,
  packages,
  onClose,
  onApproved,
}: {
  supplierId: string;
  /** The supplier's mapped effect channel name, or `null` for a supplier with
   *  no channel mapped - see the component doc comment. */
  channelName: string | null;
  item: FeedScanItem;
  packages: ContentPackageRow[];
  onClose: () => void;
  onApproved: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [chosenPackages, setChosenPackages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The exact payload the approve route will build from this item and these
  // edits - one shared function on both sides, so the table below cannot
  // promise a field the server does not write, or hide one it does.
  const edits = { title, synopsisLong: description };
  const writes = describeFeedAssetPlan(
    planFeedAsset(supplierId, item, edits),
    item,
    edits
  );

  async function approve(dismiss: Dismiss) {
    setSaving(true);
    setError(null);
    try {
      await foyerApi.approveFeedItem(supplierId, item.guid, {
        title: title.trim(),
        synopsisLong: description.trim(),
        // Never sent for a channel-mapped supplier - there is no picker to
        // have set it, and sending the empty array either way is exactly
        // what "no collection chosen" already means server-side.
        packages: channelName ? [] : chosenPackages,
      });
      toast.success(t("feedReviewView.clipApproved", { title: title.trim() }));
      dismiss(onApproved);
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(describeError(t, "approveClip", e, { title: title.trim() }));
    } finally {
      setSaving(false);
    }
  }

  return (
    // An edit of an item that already exists (the feed's own clip), so it
    // slides in from the right - on top of the scan dialog it opens from.
    <EditDrawer
      open
      onClose={onClose}
      title={t("feedReviewView.reviewTitle")}
      width={900}
      // Pinned above the buttons, never at the end of the body: the field
      // tables can run to a hundred keywords, and a refusal printed below them
      // read as "nothing happened" (2026-10-12).
      footerTop={
        error ? (
          <Typography
            variant="bodySm"
            role="alert"
            data-testid="feed-review-error"
            sx={{ color: tokens.error }}
          >
            {error}
          </Typography>
        ) : undefined
      }
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()} color="inherit">
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={!title.trim() || saving}
            onClick={() => void approve(dismiss)}
            data-testid="feed-review-approve"
          >
            {saving
              ? t("feedReviewView.approving")
              : t("feedReviewView.approve")}
          </Button>
        </>
      )}
    >
      <Stack gap={2}>
        <Box
          sx={{
            bgcolor: "#000",
            borderRadius: "10px",
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          <video
            src={item.mediaUrl}
            poster={item.thumbnailUrl ?? undefined}
            controls
            playsInline
            data-testid="feed-item-preview"
            style={{ width: "100%", maxHeight: "40vh", display: "block" }}
          />
        </Box>

        <TextField
          size="small"
          label={t("feedReviewView.title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          autoFocus
          inputProps={{ "data-testid": "feed-review-title" }}
        />
        <TextField
          size="small"
          label={t("feedReviewView.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          inputProps={{ "data-testid": "feed-review-description" }}
        />
        {channelName ? (
          <Alert severity="info" data-testid="feed-review-channel-note">
            <Trans
              t={t}
              i18nKey="feedReviewView.joinsOnApproval"
              values={{ channel: channelName }}
              components={{ strong: <strong /> }}
            />
          </Alert>
        ) : (
          <TextField
            select
            size="small"
            label={t("feedReviewView.mediaCollections")}
            value={chosenPackages}
            onChange={(e) =>
              setChosenPackages(
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : (e.target.value as unknown as string[])
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                (selected as string[])
                  .map((id) => packages.find((p) => p.id === id)?.name ?? id)
                  .join(", "),
            }}
            helperText={t("feedReviewView.noCollectionHint")}
            inputProps={{ "data-testid": "feed-review-packages" }}
          >
            {packages.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <FeedFieldTable
          title={t("feedReviewView.writtenOnApproval")}
          testId="feed-review-writes"
          rows={writes}
        />

        {item.unmapped.length > 0 && (
          <FeedFieldTable
            title={t("feedReviewView.notWritten")}
            caption={t("feedReviewView.notWrittenCaption")}
            testId="feed-review-unmapped"
            rows={item.unmapped.map((f, i) => ({
              key: `${f.field}-${i}`,
              label: f.field,
              value: f.value,
            }))}
          />
        )}
      </Stack>
    </EditDrawer>
  );
}

/**
 * A plain field/value list for the review dialog. `source` is optional: the
 * "written" table says where each value came from, the "not written" one has
 * nothing to say beyond the feed's own field name.
 */
function FeedFieldTable({
  title,
  caption,
  testId,
  rows,
}: {
  title: string;
  caption?: string;
  testId: string;
  rows: {
    key: string;
    label: string;
    value: string;
    source?: FeedAssetValueSource;
  }[];
}) {
  const { t } = useTranslation("content");
  return (
    <Box data-testid={testId}>
      <Typography
        variant="bodySm"
        sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
      >
        {title}
      </Typography>
      {caption && (
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {caption}
        </Typography>
      )}
      <TableContainer sx={{ mt: 0.5 }}>
        <Table size="small">
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} data-testid={`${testId}-${r.key}`}>
                <TableCell
                  sx={{
                    width: 150,
                    verticalAlign: "top",
                    color: tokens.inkMuted,
                    typography: "meta",
                  }}
                >
                  {r.label}
                </TableCell>
                <TableCell
                  sx={{
                    typography: "bodySm",
                    verticalAlign: "top",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {r.value}
                </TableCell>
                {r.source && (
                  <TableCell sx={{ width: 96, verticalAlign: "top" }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={r.source === "edited" ? "primary" : "default"}
                      label={t(`feedReviewView.source.${r.source}`)}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** One big number with an icon - the scan's headline, not a caption. */
/**
 * Doubles as the filter control for the table below it (`view` in
 * `FeedScanDialog`) - not a separate New/All row saying the same three
 * words again. `selected` is the one visual difference from a plain stat:
 * a brand-tinted fill and border, the same "this is the active choice"
 * language already used for a selected row elsewhere (`tokens.brandTintSoft`).
 */
function StatCard({
  icon,
  value,
  label,
  testId,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  testId: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      data-testid={testId}
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.5,
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${selected ? tokens.brand : tokens.divider}`,
        bgcolor: selected ? tokens.brandTintSoft : tokens.rowAlt,
        textAlign: "left",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": { borderColor: tokens.brand },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            typography: "display",
            fontWeight: "fontWeightBold",
            lineHeight: 1,
            color: tokens.ink,
          }}
        >
          {value}
        </Typography>
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {label}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
